/**
 * The marks in issue on a matter — `GET`/`PUT /api/matters/{id}/signs`.
 *
 * A logo-versus-logo objection was unrepresentable before this: the platform
 * held `matters.mark_text`, one nullable string, and `s3`'s `cited_marks`, a
 * list of names read out of the letter. Neither says what a sign *looks like*,
 * and for a figurative mark that is the whole case.
 *
 * The rule the whole module turns on: **a caller never says who observed a
 * mark.** `MatterSignInput` has no `observed_by` field, and the server stamps
 * the signed-in person whenever a description arrives. `docs/DEVICE_MARKS.md`
 * measured why — a device has no string to check against and the corpus has
 * none to lend, so a visual claim is accountable only for who made it.
 */
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'

import { api, API_BASE } from './client'

export type SignRole = 'SUBJECT' | 'CITED'
export type SignObserver = 'ATTORNEY' | 'MODEL'

/** One sign as the server returns it. */
export interface MatterSign {
  /**
   * The row's identity, and the only thing that survives a renumber. Echo it
   * back in a `PUT` so the server knows which sign each entry *is*: without it
   * it can only match on the ordinal, and an attorney reordering two marks
   * moves the picture of one onto the other.
   */
  id: string
  ordinal: number
  role: SignRole
  label: string
  word_element: string | null
  device_subject: string | null
  dominant: string | null
  colours: string | null
  goods: string | null
  image_sha256: string | null
  observed_by: SignObserver | null
  observed_at: string | null
  observed_method: string | null
  /** Who attested it. `null` for a model reading, which has no attestor. */
  observed_by_name: string | null
  /**
   * This description is not known to be about the picture now on file —
   * either it was attested against a different one, or it was written before
   * any picture was attached. Exact rather than heuristic: the blob store is
   * content-addressed, so this is two digests compared.
   */
  stale: boolean
}

/**
 * One sign as the attorney states it.
 *
 * No `observed_by` and no `image_sha256`: the first is the server's to stamp,
 * and the second is attached out of band by the upload door so that fixing a
 * typo in a label cannot detach a picture.
 */
export interface MatterSignInput {
  /** Which existing sign this is. Omit for a new one. */
  id?: string | null
  ordinal: number
  role: SignRole
  label: string
  word_element?: string | null
  device_subject?: string | null
  dominant?: string | null
  colours?: string | null
  goods?: string | null
}

export const signKeys = {
  signs: (matterId: string) => ['matters', matterId, 'signs'] as const,
}

export function useMatterSigns(matterId: string | null): UseQueryResult<MatterSign[]> {
  return useQuery<MatterSign[]>({
    queryKey: signKeys.signs(matterId ?? ''),
    queryFn: () => api.get<MatterSign[]>(`/api/matters/${encodeURIComponent(matterId ?? '')}/signs`),
    enabled: Boolean(matterId),
  })
}

export function useSaveSigns(matterId: string) {
  const client = useQueryClient()
  return useMutation<MatterSign[], Error, MatterSignInput[]>({
    mutationFn: (signs) =>
      api.put<MatterSign[]>(`/api/matters/${encodeURIComponent(matterId)}/signs`, { signs }),
    onSuccess: (rows) => {
      // Seeded from the server's answer rather than invalidated: the response
      // IS the new set, and a refetch would show the old one for a frame.
      client.setQueryData(signKeys.signs(matterId), rows)
    },
  })
}

export function useUploadSignImage(matterId: string) {
  const client = useQueryClient()
  return useMutation<MatterSign, Error, { ordinal: number; file: File }>({
    mutationFn: ({ ordinal, file }) => {
      const body = new FormData()
      body.append('file', file)
      return api.post<MatterSign>(
        `/api/matters/${encodeURIComponent(matterId)}/signs/${ordinal}/image`,
        body,
      )
    },
    onSuccess: () => {
      // Invalidated rather than seeded: the response is ONE row and the cache
      // holds the set. Writing one row over a list is the truncated-cache bug
      // `useSaveSigns` avoids by returning the whole set.
      void client.invalidateQueries({ queryKey: signKeys.signs(matterId) })
    },
  })
}

/** What the vision model proposed, and what the reviewer must be told. */
export interface SignDescribeResponse {
  sign: MatterSign
  /** `vlm_ocr:<model id>` — which machine said this. */
  method: string
  /** The word-element mismatch, and anything the model could not make out. */
  notes: string[]
}

/**
 * Ask a vision model what a sign's picture shows.
 *
 * A **proposal**, never an observation. The row it writes carries `MODEL` and
 * no user id — structurally incapable of being an attestation — and the gate
 * BLOCKS a draft that cites one. An attorney turns it into an observation by
 * opening the picture, reading the description, and saving it.
 */
export function useDescribeSign(matterId: string) {
  const client = useQueryClient()
  return useMutation<SignDescribeResponse, Error, { ordinal: number }>({
    mutationFn: ({ ordinal }) =>
      api.post<SignDescribeResponse>(
        `/api/matters/${encodeURIComponent(matterId)}/signs/${ordinal}/describe`,
      ),
    onSuccess: () => {
      // Invalidated rather than seeded, for the reason `useUploadSignImage`
      // gives: the response carries ONE row and the cache holds the set.
      void client.invalidateQueries({ queryKey: signKeys.signs(matterId) })
    },
  })
}

/**
 * Where the browser fetches a sign's picture.
 *
 * Content-addressed, so the URL is stable for those exact bytes and the server
 * sends a year of private cache. Not a data URI: the bytes are a client's
 * document and belong behind the same session as everything else.
 */
export function signImageUrl(matterId: string, sha256: string): string {
  return `${API_BASE}/api/matters/${encodeURIComponent(matterId)}/blobs/${encodeURIComponent(sha256)}`
}

/** What the screen says about who looked, in one place rather than three. */
export function observerLabel(sign: MatterSign): string {
  if (sign.observed_by === 'ATTORNEY') {
    return sign.observed_by_name ? `described by ${sign.observed_by_name}` : 'described by an attorney'
  }
  if (sign.observed_by === 'MODEL') {
    return `read by ${sign.observed_method ?? 'a model'} — not an attestation`
  }
  return 'nobody has described this mark yet'
}
